import * as Headless from '@headlessui/react';
import { Pagination } from '@heroui/react';
import { type FC, useEffect, useState } from 'react';
import { Label } from '@/primitives/fieldset.tsx';
import { Listbox, ListboxLabel, ListboxOption } from '@/primitives/listbox.tsx';

export function usePagination<T>({
  data,
  pageSizes = [10, 25, 45],
  resourceName = 'results',
}: {
  data: T[];
  pageSizes?: number[];
  resourceName?: string;
}): PaginatorProps<T> {
  const [current, setCurrent] = useState(0);
  const [pageSize, setPageSize] = useState(pageSizes[0]);
  const [pagedData, setPagedData] = useState(data ?? []);
  const [startIndex, setStartIndex] = useState(0);
  const [pageCount, setPageCount] = useState(Math.ceil((data ?? []).length / pageSize));

  useEffect(() => {
    const newPageCount = Math.ceil((data ?? []).length / pageSize);

    if (current >= newPageCount && newPageCount > 0) {
      setCurrent(0);
      return;
    }

    const start = current * pageSize;
    const end = start + pageSize;
    setPagedData((data ?? []).slice(start, end));
    setPageCount(newPageCount);
    setStartIndex(start);
  }, [current, pageSize, data]);

  return {
    resourceName,
    currentPage: current,
    pageSize: pageSize,
    totalItems: data?.length,
    pageSizes: pageSizes,
    pagedData: pagedData,
    onPageChange: setCurrent,
    startIndex,
    setPageSize,
    pageCount,
  };
}

interface PaginatorProps<T> {
  resourceName: string;
  pageCount: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  totalItems: number;
  startIndex: number;
  pageSizes: number[];
  pagedData: T[];
  pageSize: number;
  setPageSize: (size: number) => void;
}

export const Paginator: FC<PaginatorProps<any>> = ({
  resourceName,
  pageCount,
  currentPage,
  onPageChange,
  totalItems,
  pageSizes,
  startIndex,
  pagedData,
  pageSize,
  setPageSize,
}) => {
  const hasMoreThanOnePage = pageCount > 1;
  return (
    <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between bg-white p-3'>
      <div className='flex flex-col lg:flex-row lg:flex-1 lg:items-center lg:justify-between'>
        <div>
          {hasMoreThanOnePage && (
            <p className='text-meta text-xs'>
              showing {startIndex + 1} to {startIndex + pagedData.length}
            </p>
          )}
          <p className='text-meta !text-xs'>
            {totalItems} total {resourceName}
          </p>
        </div>
        <div className='flex flex-col lg:flex-row gap-10'>
          <Headless.Field className='flex items-baseline gap-2 min-w-96'>
            <Label className='min-w-fit text-right text-meta !text-xs'>Items per page:</Label>
            <Listbox
              onChange={setPageSize}
              value={pageSize}
              defaultValue={pageSizes[0]}
              className='max-w-24'
            >
              {pageSizes.map(size => (
                <ListboxOption key={size} value={size}>
                  <ListboxLabel>{size}</ListboxLabel>
                </ListboxOption>
              ))}
            </Listbox>
          </Headless.Field>
          <Pagination
            total={pageCount}
            page={currentPage + 1}
            onChange={page => onPageChange(page - 1)}
            showControls
          />
        </div>
      </div>
    </div>
  );
};
